import React, { forwardRef, useState } from 'react';
import { cn } from '../../utils/cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
  hover?: 'lift' | 'glow' | 'scale' | 'none';
  animate?: boolean;
  stagger?: number;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      hover = 'lift',
      animate = true,
      stagger = 0,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    const baseClasses = 'card';

    const variantClasses = {
      default: 'bg-white border border-neutral-200 shadow-sm',
      elevated: 'bg-white border-0 shadow-lg',
      outlined: 'bg-white border-2 border-neutral-300 shadow-none',
    };

    const paddingClasses = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    };

    const hoverClasses = {
      lift: 'card-hover',
      glow: 'glow',
      scale: 'hover:scale-105',
      none: '',
    };

    const staggerClasses = {
      0: '',
      1: 'animate-stagger-1',
      2: 'animate-stagger-2',
      3: 'animate-stagger-3',
      4: 'animate-stagger-4',
      5: 'animate-stagger-5',
    };

    return (
      <div
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          paddingClasses[padding],
          hoverClasses[hover],
          staggerClasses[stagger as keyof typeof staggerClasses],
          {
            'animate-fade-in': animate,
            'animate-scale-in': animate && stagger === 0,
          },
          'rounded-xl transition-all duration-300 ease-spring',
          className,
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'card-header',
          'px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('card-body', 'px-6 py-4', className)} {...props}>
        {children}
      </div>
    );
  },
);

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'card-footer',
          'px-6 py-4 border-t border-neutral-200 bg-neutral-50',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardBody.displayName = 'CardBody';
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardBody, CardFooter };
